import React, { useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import Router from 'next/router';
import api from 'api';
import { breakpoints } from 'styles';
import {
  ApiDownloadButton,
  DisplayFormattedDate,
  EmptyIndicator,
  Pagination,
  Search,
  User,
  UserCoursesProgress
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { StatCount } from 'components/common/stats';
import { UsersSendMessage } from 'components/users';

import type { ProgressData, User as UserInterface } from 'shared-types';

interface GroupUpdateUsers {
  page?: number;
  search?: string;
  showLoading?: boolean;
}

interface State {
  page: number;
  pages: number;
  search: string;
  users: (UserInterface & { progressData: ProgressData })[];
  usersCount: number;
}

const GroupFollow = () => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { group }
  } = useReduxState();

  const [loading, setLoading] = useState<boolean>(true);
  const [state, setState] = useState<State>({
    page: 1,
    pages: 0,
    search: '',
    users: null,
    usersCount: 0
  });

  useEffect(() => {
    updateUsers({});
  }, []);

  const handleUserClick = (ev: React.PointerEvent<HTMLDivElement>) => {
    const as = ev.currentTarget.getAttribute('data-as');
    const href = ev.currentTarget.getAttribute('data-href');
    Router.push(href, as);
  };

  const updateUsers = async ({
    page,
    search,
    showLoading = true
  }: GroupUpdateUsers) => {
    try {
      if (showLoading) setLoading(true);

      const pageValue = page || state.page;
      const searchValue = search !== undefined ? search : state.search;

      const users = await api.get(
        `/admin/groups/${group.id}/participants` +
          `?page=${pageValue}` +
          (searchValue ? `&search=${searchValue}` : '')
      );

      setState({
        ...state,
        page: pageValue,
        pages: users.data.pages,
        search: searchValue,
        users: users.data.users,
        usersCount: users.data.usersCount
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="users">
      <Box
        headerContent={
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            <UsersSendMessage
              buttonSize="header"
              id="group-follow-send-message"
              style={{ marginRight: 10 }}
              userSelectionParams={{
                excludeUsersWithoutEmail: true,
                routerQueryParams: { groupId: `${group.id}` },
                search: state.search,
                userSelector: 'emails'
              }}
            />

            <ApiDownloadButton
              category="success"
              icon="file-excel"
              filename={`${group.title}.xlsx`}
              size="header"
              text={messages.groupsDownloadSummary}
              url={`/admin/groups/${group.id}/excel-export?language=${language}`}
            />
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        subHeaderContent={
          <div
            style={{
              flexGrow: 1,
              minWidth: 0
            }}
          >
            <Search
              onSearch={updateUsers}
              placeholder={messages.usersSearchUsers}
            />
          </div>
        }
        title={
          <>
            {messages.commonUsers}
            <StatCount
              isLarge={true}
              style={{ display: 'inline-block', marginLeft: 5 }}
              value={state.usersCount}
            />
          </>
        }
      >
        {state.users && state.users.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonUser}</BoxTableCell>
              <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>
              <BoxTableCell>{messages.groupsCourses}</BoxTableCell>
            </BoxTableHeader>

            {state.users.map((user) => (
              <BoxTableRow
                key={user.id}
                data-as={`/groups/${group.id}/follow/${user.id}`}
                data-href="/groups/[groupId]/follow/[userId]"
                onClick={handleUserClick}
              >
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User user={user} />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  label={messages.usersLastLogin}
                >
                  {user.lastLogin ? (
                    <DisplayFormattedDate date={user.lastLogin} />
                  ) : null}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonProgress}
                  shrink={true}
                >
                  <UserCoursesProgress progressData={user.progressData} />
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={state.page}
        numberOfPages={state.pages}
        onPageChange={updateUsers}
      />
    </div>
  );
};

export default GroupFollow;
